import { EntityKind } from "../../../../models/entityKind";
import { sessionStorageUwidFormat } from "../../../../utils/sessionStorageUwidFormat";
import {
  CopyWellListFormatT,
  CopyWellListQuoteTypeT,
  IWellGroupMap,
  IWellListState,
  SelectedWellT,
  UwidFormatT,
  WellInfoT,
  WellListDispatchT
} from "../WellListContext";

type WellListReducerActionsT =
  | { type: "update well list"; wells: WellInfoT[] }
  | { type: "update selected well"; well?: SelectedWellT }
  | { type: "update well group map"; mapping?: IWellGroupMap }
  | { type: "update setting uwi format"; format: UwidFormatT }
  | { type: "update setting copy format"; format: CopyWellListFormatT }
  | { type: "update setting scroll to well"; enabled: boolean }
  | { type: "update setting show focus color"; enabled: boolean }
  | { type: "update setting copy quote type"; quoteType: CopyWellListQuoteTypeT }
  | { type: "update entity kind"; entityKind: EntityKind }
  | { type: "update total count"; count: number }
  | { type: "append wells"; wells: WellInfoT[] }
  | { type: "set well page"; pageIndex: number; wells: WellInfoT[] }
  | { type: "reset well pages" };

const wellListReducer = (draft: IWellListState, action: WellListReducerActionsT) => {
  switch (action.type) {
    case "update well list": {
      draft.wells = action.wells;
      break;
    }
    case "update selected well": {
      // keep track of the last 10 selected wells.
      if (draft.selectedWell) {
        const existingIndex = draft.previouslySelectedWells.findIndex(
          (w) => w.Uwi === draft.selectedWell.Uwi
        );
        if (existingIndex !== -1) {
          draft.previouslySelectedWells.splice(existingIndex, 1);
        }

        draft.previouslySelectedWells.unshift(draft.selectedWell);
        if (draft.previouslySelectedWells.length > 10) {
          draft.previouslySelectedWells.pop();
        }
      }
      draft.selectedWell = action.well;
      break;
    }
    case "update well group map": {
      draft.wellGroupMap = action.mapping;
      break;
    }
    case "update setting uwi format": {
      draft.settings.uwidFormat = action.format;
      sessionStorageUwidFormat.set(action.format);
      break;
    }
    case "update setting copy format": {
      draft.settings.copyFormat = action.format;
      break;
    }
    case "update setting scroll to well": {
      draft.settings.scrollToSelected = action.enabled;
      break;
    }
    case "update setting show focus color": {
      draft.settings.showFocusColor = action.enabled;
      break;
    }
    case "update setting copy quote type": {
      draft.settings.copyQuoteType = action.quoteType;
      break;
    }
    case "update entity kind": {
      draft.entityKind = action.entityKind;
      break;
    }
    case "update total count": {
      draft.totalCount = action.count;
      break;
    }
    case "append wells": {
      // concat, not push(...spread): action.wells can be ~1M elements and
      // spreading as call args blows the engine argument-count limit.
      draft.wells = draft.wells.concat(action.wells);
      // Once appended, these wells live in the contiguous `wells` array —
      // the sparse page cache for this filter is now redundant.
      draft.wellPages = {};
      break;
    }
    case "set well page": {
      draft.wellPages[action.pageIndex] = action.wells;
      break;
    }
    case "reset well pages": {
      draft.wellPages = {};
      break;
    }
    default: {
      throw new Error("invalid well list reducer action");
    }
  }
};

// module functions
const updateWellList = (dispatch: WellListDispatchT, wells: WellInfoT[]) =>
  dispatch({ type: "update well list", wells });

const updateSelectedWell = (dispatch: WellListDispatchT, well?: SelectedWellT) =>
  dispatch({ type: "update selected well", well });

const updateWellGroupMap = (dispatch: WellListDispatchT, mapping?: IWellGroupMap) =>
  dispatch({ type: "update well group map", mapping });

const updateSettingUwiFormat = (dispatch: WellListDispatchT, format: UwidFormatT) =>
  dispatch({ type: "update setting uwi format", format });

const updateSettingCopyFormat = (
  dispatch: WellListDispatchT,
  format: CopyWellListFormatT
) => dispatch({ type: "update setting copy format", format });

const updateSettingCopyQuoteType = (
  dispatch: WellListDispatchT,
  quoteType: CopyWellListQuoteTypeT
) => dispatch({ type: "update setting copy quote type", quoteType });

const updateSettingScrollToWell = (dispatch: WellListDispatchT, enabled: boolean) =>
  dispatch({ type: "update setting scroll to well", enabled });

const updateSettingShowFocusColor = (dispatch: WellListDispatchT, enabled?: boolean) =>
  dispatch({ type: "update setting show focus color", enabled });

const updateEntityKind = (dispatch: WellListDispatchT, entityKind: EntityKind) =>
  dispatch({ type: "update entity kind", entityKind });

const updateTotalCount = (dispatch: WellListDispatchT, count: number) =>
  dispatch({ type: "update total count", count });

const appendWells = (dispatch: WellListDispatchT, wells: WellInfoT[]) =>
  dispatch({ type: "append wells", wells });

const setWellPage = (
  dispatch: WellListDispatchT,
  pageIndex: number,
  wells: WellInfoT[]
) => dispatch({ type: "set well page", pageIndex, wells });

const resetWellPages = (dispatch: WellListDispatchT) =>
  dispatch({ type: "reset well pages" });

export {
  updateSelectedWell,
  updateSettingUwiFormat,
  updateSettingCopyFormat,
  updateSettingCopyQuoteType,
  updateSettingScrollToWell,
  updateSettingShowFocusColor,
  updateWellGroupMap,
  updateWellList,
  updateEntityKind,
  updateTotalCount,
  appendWells,
  setWellPage,
  resetWellPages,
  wellListReducer
};

export type { WellListReducerActionsT };
